import { striptags } from '@webapp/common/lib/utils';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { SurveyStore } from '@webapp/common/resources/entities';
import { PIPING_SENDER_QUESTION_TYPES } from '@webapp/common/resources/piping-constants';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

import { getQ } from '@webapp/survey/src/machine/lib';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';

export const calcSurveyProgress = (questions: Array<QuestionActorRef>): number => {
    const eligibleQuestions = questions.filter((q) => getQ(q).type !== QuestionType.TEXT_BLOCK);
    const changedQuestions = eligibleQuestions.filter((q) => getQ(q).dirty);

    return changedQuestions.length / eligibleQuestions.length;
};

export const getPipingAnswers = (state: SurveyStore) => {
    const questionsWithPiping = state.questions.filter((q) =>
        PIPING_SENDER_QUESTION_TYPES.includes(q.getSnapshot().context.type),
    );

    const answers: Record<number, string> = questionsWithPiping.reduce((acc, q) => {
        let answer = '';
        const qContext = q.getSnapshot().context;

        switch (qContext.type) {
            case QuestionType.ONE_OF_LIST:
            case QuestionType.TEST_ONE_OF_LIST:
                const foundedAnswerRaw = qContext.answersActors
                    .find((answerActor) => answerActor.getSnapshot().context?.response?.getSnapshot().context.value)
                    ?.getSnapshot().context;
                let foundedAnswer = foundedAnswerRaw;
                while (foundedAnswer?.forwardParent) {
                    const foundedQ = state.questions.find((q) =>
                        q
                            .getSnapshot()
                            .context.answersActors.find(
                                (a) => a.getSnapshot().context.id === foundedAnswer.forwardParent,
                            ),
                    );
                    foundedAnswer = foundedQ
                        ?.getSnapshot()
                        .context.answersActors.find(
                            (answerActor) => answerActor.getSnapshot().context.id === foundedAnswer.forwardParent,
                        )
                        ?.getSnapshot().context;
                    if (foundedAnswer) {
                        foundedAnswer = { ...foundedAnswer, id: foundedAnswerRaw.id };
                    }
                }
                if (foundedAnswer?.type === AnswerType.SELF) {
                    answer = foundedAnswer.response?.getSnapshot().context.extra?.toString() || '';
                } else {
                    answer = foundedAnswer?.value?.[0] || '';
                }
                break;

            case QuestionType.FEW_OF_LIST:
            case QuestionType.TEST_FEW_OF_LIST:
                answer = qContext.answersActors
                    .filter((answerActor) => answerActor.getSnapshot().context.response?.getSnapshot().context.value)
                    ?.map((answ) => {
                        let foundedAnswer = answ.getSnapshot().context;
                        while (foundedAnswer?.forwardParent) {
                            const foundedQ = state.questions.find((q) =>
                                q
                                    .getSnapshot()
                                    .context.answersActors.find(
                                        (a) => a.getSnapshot().context.id === foundedAnswer.forwardParent,
                                    ),
                            );
                            foundedAnswer = foundedQ
                                ?.getSnapshot()
                                .context.answersActors.find(
                                    (answerActor) =>
                                        answerActor.getSnapshot().context.id === foundedAnswer.forwardParent,
                                )
                                ?.getSnapshot().context;
                            if (foundedAnswer) {
                                foundedAnswer = { ...foundedAnswer, id: answ.getSnapshot().context.id };
                            }
                        }
                        return foundedAnswer?.type === AnswerType.SELF
                            ? foundedAnswer?.response?.getSnapshot().context.extra?.toString()
                            : foundedAnswer?.value?.[0] || '';
                    })
                    .join(', ');
                break;
            case QuestionType.FIRST_CLICK:
                answer = qContext.answersActors
                    .filter((answerActor) =>
                        qContext.clicks.find((click) => click.answerId === answerActor.getSnapshot().context.id),
                    )
                    .map((actor) => actor.getSnapshot().context.value?.[0])
                    .join(', ');
                break;
            case QuestionType.FIGMA_TEST: {
                const figmaResult = qContext.responseActor.getSnapshot().context.value as unknown as {
                    success: boolean | null;
                    finalScreenName: string;
                } | null;
                if (figmaResult) {
                    answer =
                        figmaResult.success === true
                            ? 'Успешно'
                            : figmaResult.success === false
                            ? 'Сдался'
                            : figmaResult.finalScreenName || '';
                }
                break;
            }
            case QuestionType.DROPDOWN_LIST:
                if (qContext.params.dropdownType !== 'multi') {
                    const answerId = qContext.responseActor.getSnapshot().context.value;
                    answer =
                        qContext.answersActors
                            .find((answerActor) => answerActor.getSnapshot().context.id === answerId)
                            ?.getSnapshot().context.value?.[0] || '';
                } else {
                    answer = qContext.answersActors
                        .filter(
                            (answerActor) => answerActor.getSnapshot().context.response?.getSnapshot().context.value,
                        )
                        ?.map((answ) => answ.getSnapshot().context.value?.[0] || '')
                        .join(', ');
                }
                break;
            case QuestionType.SHORT_TEXT:
                const value = qContext.responseActor.getSnapshot().context.value;
                answer = value === CANTRATE_ID ? '' : value?.toString();
                break;
            case QuestionType.NAME:
                const groupsWithAnswers = qContext.groupsActors.map((groupActor) => {
                    const { responses } = groupActor.getSnapshot().context;
                    return Object.keys(responses).map((key) => String(responses[key]?.getSnapshot().context?.value));
                });
                answer = qContext.answersActors
                    .map((_, idx) => {
                        return [
                            groupsWithAnswers?.[0]?.[idx],
                            groupsWithAnswers?.[1]?.[idx],
                            groupsWithAnswers?.[2]?.[idx],
                        ]
                            .filter(Boolean)
                            .join(' ');
                    })
                    .join(', ');

                break;
            case QuestionType.SCALE:
                const group = qContext.groupsActors.find((groupActor) => {
                    return qContext.responseActor.getSnapshot().context.value === groupActor.getSnapshot().context.id;
                });
                if (group) {
                    answer = group.getSnapshot().context.name;
                }
                break;
            case QuestionType.NPS:
                if (typeof qContext.responseActor.getSnapshot().context.value === 'number') {
                    answer = String(qContext.responseActor.getSnapshot().context.value);
                }
                break;
        }
        if (answer) {
            acc[q.getSnapshot().context.id] = striptags(answer);
        }
        return acc;
    }, {});
    return answers;
};
